import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import { mdiEmail } from '@mdi/js';

import {
    CurrentUserDocument,
    IBasicUserFragment,
    IPortfolioItemMatrixFragment,
    IRole,
} from 'graphql/types';
import { TableCell, TableHead, useSelection } from 'common/components/Table';
import { BoxLoader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import { InlineStickyWrapper } from 'common/components/InlineStickyWrapper';
import { FilterBar } from 'common/components/FilterBar';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { useSkillUserList } from 'skill/hooks/useSkillUserList';
import { IFilterFormItem, IRow } from 'common/types';
import { useSkillList } from 'skill/hooks/useSkillList';
import { useSkillReportList } from 'skill/hooks/useSkillReportList';
import { useSkillReportColumnExpansion } from 'skill/hooks/useSkillReportColumnExpansion';
import { Checkbox } from 'common/components/Checkbox';
import {
    SkillReportMatrixCertificateHeaderCell,
    SkillReportMatrixRow,
} from 'skill/components/SkillReportMatrix';
import { IconButton } from 'common/components/IconButton';
import { SkillCertificateOfferEventDialog } from 'skill/components/SkillCertificateOfferEventDialog';
import { ESkillCertificateOfferEventDialogMode } from 'skill/types';
import { UserEmailDialog } from 'user/components/UserEmailDialog';
import { getUrl } from 'route/utils/getUrl';

export const SkillReportMatrix = () => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const [userEmailDialogOpen, setUserEmailDialogOpen] = useState(false);
    const [eventDialogMode, setEventDialogMode] =
        useState<ESkillCertificateOfferEventDialogMode | null>(null);

    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const isManager = !!currentUserData?.currentUser?.roles?.includes(
        IRole.Manager
    );

    const {
        skills,
        loading: loadingSkills,
        filters: skillFilters = [],
    } = useSkillList();

    const theme = useTheme();

    // Calculate the total height of the navigation bar
    const baseNavHeights = (theme.mixins.toolbar.height as number) || 0;
    const navHeights = `calc(${baseNavHeights}px + var(--top-offset, 0px))`;

    // Skill filter for report
    const skillFilterIds = useMemo(
        () => skills.map((skill) => skill.id),
        [skills]
    );

    const {
        users,
        loading: loadingUsers,
        paginationSettings,
        filters: userFilters,
        filterValues,
        isCompliant,
        onSearch,
        onFilterChange: onUserFilterChange,
        setPage,
    } = useSkillUserList({ skillIds: skillFilterIds });

    const { ...selectionProps } = useSelection(users);
    const {
        rows: userRows,
        selected,
        onSelectAll,
        setSelected,
    } = selectionProps;

    const userIds = useMemo(() => users.map((user) => user.id), [users]);

    const {
        items: skillReportItems,
        loading: loadingReports,
        refetch,
    } = useSkillReportList({
        queryVariables: { userIds, skillIds: skillFilterIds, isCompliant },
        skip: !userIds.length,
    });

    const {
        expandedSkillIds,
        expandedSkillIdsArray,
        certificatesBySkill,
        userCertificates,
        loadingCertificates,
        refetchCertificates,
        noCertsDialogSkillId,
        toggleSkillExpansion,
        closeNoCertsDialog,
    } = useSkillReportColumnExpansion({ userIds });

    const handleSearch = (searchValue: string) => {
        setPage(1);
        setSelected([]);
        onSearch?.(searchValue);
    };

    const loading = loadingReports || loadingSkills || loadingUsers;

    const stickyOffsetLeft = {
        xs: 'auto',
        sm: theme.spacing(3),
    };

    const skillReportRows = useMemo(() => {
        const rows = new Map<
            string,
            {
                row: IRow;
                user: IBasicUserFragment;
                portfolioItems: IPortfolioItemMatrixFragment[];
            }
        >();

        users.forEach((user, index) => {
            rows.set(user.id, {
                row: userRows[index],
                user: user,
                portfolioItems: [
                    ...skillReportItems.filter(
                        (item) => item.user?.id === user.id
                    ),
                ],
            });
        });

        return rows;
    }, [skillReportItems, userRows, users]);

    const filters = [...skillFilters, ...userFilters] as IFilterFormItem[];
    // The user query already filters by skill compliancy, so every returned
    // user belongs in the matrix and the pagination count matches the rows.
    const skillReportRowList = Array.from(skillReportRows.values());
    const hasSkillReport = !!skills.length && !!skillReportRowList.length;

    const tableActions = (
        <Stack direction="row" spacing={0.5}>
            <DropdownMenu
                anchor={
                    <IconButton
                        aria-label={translate('skillsMatrix.mailButtonText')}
                        color="inherit"
                        iconPath={mdiEmail}
                        iconSize="2.4rem"
                    />
                }
                placement="bottom-start"
            >
                <MenuItem
                    onClick={() =>
                        setEventDialogMode(
                            ESkillCertificateOfferEventDialogMode.Enrollment
                        )
                    }
                >
                    {translate('skillsMatrix.enrollInOffer')}
                </MenuItem>

                <MenuItem
                    onClick={() =>
                        setEventDialogMode(
                            ESkillCertificateOfferEventDialogMode.Notify
                        )
                    }
                >
                    {translate('skillsMatrix.notifyAboutOffer')}
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        setUserEmailDialogOpen(true);
                    }}
                >
                    {translate('skillsMatrix.personalMessage')}
                </MenuItem>
            </DropdownMenu>
        </Stack>
    );

    const isAllSelected = useMemo(
        () => selected.length === users.length,
        [selected, users]
    );

    const hasSelection = useMemo(() => selected.length > 0, [selected]);

    return (
        <>
            <FilterBar
                useFilterDrawer
                filterDrawerProps={{
                    title: translate('reportPage.filterFormSkill.title'),
                    description: translate(
                        'reportPage.filterFormSkill.description'
                    ),
                    formProps: {
                        onReset: (values) => {
                            setPage(1);
                            setSelected([]);
                            onUserFilterChange(values);
                        },
                    },
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onFiltersChange={(values) => {
                    setPage(1);
                    setSelected([]);
                    onUserFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !hasSkillReport && (
                <Box sx={{ mt: 8, textAlign: 'center' }}>
                    <Typography>{translate('noSkillsReports')}</Typography>
                </Box>
            )}

            {!loading && hasSkillReport && (
                <>
                    <TableContainer sx={{ overflowX: 'initial', mt: 4 }}>
                        <Table stickyHeader>
                            {!!skills.length && (
                                <TableHead>
                                    <TableRow sx={{ verticalAlign: 'bottom' }}>
                                        <TableCell
                                            padding="checkbox"
                                            sx={{
                                                top: navHeights,
                                                zIndex: 5,
                                                position: 'sticky',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={
                                                    !isAllSelected &&
                                                    hasSelection
                                                }
                                                onChange={onSelectAll}
                                            />
                                        </TableCell>

                                        <TableCell
                                            colSpan={0}
                                            sx={{
                                                top: navHeights,
                                                zIndex: 5,
                                                position: 'sticky',
                                                left: stickyOffsetLeft,
                                            }}
                                        >
                                            {!hasSelection && (
                                                <Box
                                                    sx={{
                                                        pb: '3px', // Align with checkbox
                                                    }}
                                                >
                                                    {translate('participant')}
                                                </Box>
                                            )}

                                            {hasSelection && (
                                                <Stack
                                                    useFlexGap
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        alignItems: 'center',
                                                        pb: '1px', // Align with checkbox
                                                    }}
                                                >
                                                    <Typography
                                                        fontWeight={700}
                                                        variant="body2"
                                                    >
                                                        {translate(
                                                            'selectedCount',
                                                            {
                                                                value: selected.length,
                                                            }
                                                        )}
                                                    </Typography>

                                                    <Divider
                                                        flexItem
                                                        orientation="vertical"
                                                        sx={{ ml: 1 }}
                                                    />

                                                    <Box
                                                        sx={{
                                                            height: '100%',
                                                        }}
                                                    >
                                                        {tableActions}
                                                    </Box>
                                                </Stack>
                                            )}
                                        </TableCell>

                                        {skills.map((skill) => {
                                            const isExpanded =
                                                expandedSkillIds.has(skill.id);
                                            const cachedCertificates =
                                                certificatesBySkill.get(
                                                    skill.id
                                                ) || [];
                                            const isLoading =
                                                isExpanded &&
                                                loadingCertificates;
                                            // While the per-user query is in
                                            // flight, treat the cached
                                            // canonical list as empty so
                                            // only one placeholder slot
                                            // renders (matching the initial
                                            // expansion's loading look).
                                            const certificates = isLoading
                                                ? []
                                                : cachedCertificates;
                                            // Reserved column for loading and the first certificate to make animation act natural
                                            const showFirstSlot =
                                                isExpanded ||
                                                certificates.length > 0;
                                            // Remaining certificate columns rendered after the first slot
                                            const additionalCerts =
                                                certificates.slice(1);

                                            return (
                                                <React.Fragment key={skill.id}>
                                                    <TableCell
                                                        vertical
                                                        sx={{
                                                            top: navHeights,
                                                            zIndex: 4,
                                                            left: stickyOffsetLeft,
                                                            borderLeftStyle:
                                                                'solid',
                                                            borderLeftWidth:
                                                                isExpanded
                                                                    ? '1px'
                                                                    : 0,
                                                            borderLeftColor:
                                                                'divider',
                                                            transition:
                                                                'border-left-width 0.3s ease',
                                                        }}
                                                    >
                                                        <Tooltip
                                                            title={skill.name}
                                                        >
                                                            <Box
                                                                component="button"
                                                                sx={{
                                                                    background:
                                                                        'none',
                                                                    border: 0,
                                                                    p: 0,
                                                                    font: 'inherit',
                                                                    color: 'inherit',
                                                                    cursor: 'pointer',
                                                                    userSelect:
                                                                        'none',
                                                                }}
                                                                type="button"
                                                                onClick={() =>
                                                                    toggleSkillExpansion(
                                                                        skill.id
                                                                    )
                                                                }
                                                            >
                                                                {skill.shortName ||
                                                                    skill.name.substring(
                                                                        0,
                                                                        12
                                                                    )}
                                                            </Box>
                                                        </Tooltip>
                                                    </TableCell>

                                                    {showFirstSlot && (
                                                        <SkillReportMatrixCertificateHeaderCell
                                                            certificate={
                                                                certificates[0]
                                                            }
                                                            columnIndex={0}
                                                            isExpanded={
                                                                isExpanded
                                                            }
                                                            isLast={
                                                                additionalCerts.length ===
                                                                0
                                                            }
                                                            isLoading={
                                                                isLoading
                                                            }
                                                            key={`first-${skill.id}`}
                                                            navHeights={
                                                                navHeights
                                                            }
                                                        />
                                                    )}

                                                    {additionalCerts.map(
                                                        (
                                                            certificate,
                                                            index
                                                        ) => (
                                                            <SkillReportMatrixCertificateHeaderCell
                                                                certificate={
                                                                    certificate
                                                                }
                                                                columnIndex={
                                                                    index + 1
                                                                }
                                                                isExpanded={
                                                                    isExpanded
                                                                }
                                                                isLast={
                                                                    index ===
                                                                    additionalCerts.length -
                                                                        1
                                                                }
                                                                key={
                                                                    certificate.id
                                                                }
                                                                navHeights={
                                                                    navHeights
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                            )}

                            {!loadingReports && (
                                <TableBody>
                                    {skillReportRowList.map(
                                        ({ row, user, portfolioItems }) => {
                                            const { onSelect, isSelected } =
                                                row;

                                            return (
                                                <SkillReportMatrixRow
                                                    certificatesBySkill={
                                                        certificatesBySkill
                                                    }
                                                    expandedSkillIds={
                                                        expandedSkillIds
                                                    }
                                                    isSelected={isSelected}
                                                    key={user.id}
                                                    loadingCertificates={
                                                        loadingCertificates
                                                    }
                                                    portfolioItems={
                                                        portfolioItems
                                                    }
                                                    skills={skills}
                                                    user={user}
                                                    userCertificates={
                                                        userCertificates.get(
                                                            user.id
                                                        ) || new Map()
                                                    }
                                                    onSelect={onSelect}
                                                    onUpdate={() => {
                                                        refetch();

                                                        if (
                                                            !!expandedSkillIdsArray.length
                                                        ) {
                                                            refetchCertificates();
                                                        }

                                                        if (!!selected.length) {
                                                            onSelectAll();
                                                        }
                                                    }}
                                                />
                                            );
                                        }
                                    )}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>

                    <UserEmailDialog
                        open={userEmailDialogOpen}
                        userIds={selected}
                        onClose={() => setUserEmailDialogOpen(false)}
                        onComplete={() => {
                            setUserEmailDialogOpen(false);

                            onSelectAll();
                        }}
                    />

                    <SkillCertificateOfferEventDialog
                        mode={eventDialogMode ?? undefined}
                        open={!!eventDialogMode}
                        skillIds={skillFilterIds}
                        userIds={selected}
                        onClose={() => setEventDialogMode(null)}
                        onComplete={onSelectAll}
                    />

                    <ConfirmDialog
                        confirmText={
                            isManager
                                ? translate(
                                      'skillsMatrix.noCertificatesDialog.skillButton'
                                  )
                                : undefined
                        }
                        open={!!noCertsDialogSkillId}
                        title={translate(
                            'skillsMatrix.noCertificatesDialog.title'
                        )}
                        onCancel={closeNoCertsDialog}
                        onClose={closeNoCertsDialog}
                        onConfirm={() => {
                            if (isManager && noCertsDialogSkillId) {
                                navigate(
                                    getUrl('SKILL_EDIT', {
                                        id: noCertsDialogSkillId,
                                    })
                                );
                            }

                            closeNoCertsDialog();
                        }}
                    >
                        {isManager
                            ? translate(
                                  'skillsMatrix.noCertificatesDialog.textManager'
                              )
                            : translate(
                                  'skillsMatrix.noCertificatesDialog.textGroupManager'
                              )}
                    </ConfirmDialog>
                </>
            )}

            {loading && <BoxLoader />}

            {!loading && !!paginationSettings && hasSkillReport && (
                <Box sx={{ mt: 4 }}>
                    <InlineStickyWrapper fullWidth leftOffset={3}>
                        <Pagination
                            page={paginationSettings.page}
                            pageAmount={paginationSettings.pageAmount}
                            totalsAmount={paginationSettings.count}
                            totalsText={translate(
                                'reportPage.skillPagination.participants',
                                {
                                    count: paginationSettings.count,
                                }
                            )}
                            onChange={(page: number) => {
                                setPage?.(page);
                            }}
                        />
                    </InlineStickyWrapper>
                </Box>
            )}
        </>
    );
};
